import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import type { ApiFetchResponse } from '@terminal/utils/api-fetch/apiFetch';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from '@terminal/utils/pkce/pkce';
import { useEffect } from 'react';
import { useConnectionToast } from './useConnectionToast';

interface OAuthCodeConnectBody {
  extra: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
  };
}

interface UseOAuthPkceConnectProps {
  serviceName: string;
  displayName: string;
  redirectUri: string;
  buildAuthorizeUrl: (args: {
    state: string;
    codeChallenge: string;
    service: string;
  }) => string;
  postConnect: (
    body: OAuthCodeConnectBody,
    service: string
  ) => Promise<ApiFetchResponse<{ connected: boolean }>>;
  deleteDisconnect: (service: string) => Promise<{ data?: unknown }>;
  refetchServices: () => void;
}

const getErrorMessage = (fallback: string, rawMessage?: string) => {
  if (!rawMessage) {
    return fallback;
  }

  try {
    const { message } = JSON.parse(rawMessage);
    return typeof message === 'string' && message ? message : fallback;
  } catch {
    return rawMessage;
  }
};

export const useOAuthPkceConnect = ({
  serviceName,
  displayName,
  redirectUri,
  buildAuthorizeUrl,
  postConnect,
  deleteDisconnect,
  refetchServices,
}: UseOAuthPkceConnectProps) => {
  const showConnectionToast = useConnectionToast();
  const { params, deleteQueryParam } = useQueryParams<{
    code: string;
    state: string;
  }>();

  const codeVerifierKey = `${serviceName}_pkce_code_verifier`;
  const stateKey = `${serviceName}_pkce_state`;
  const serviceKey = `${serviceName}_pkce_service`;
  const loginFailedMessage = `${displayName} login failed`;
  const connectionFailedMessage = `${displayName} connection failed`;

  const connect = async (service = serviceName) => {
    const codeVerifier = generateCodeVerifier();
    const state = generateState();

    try {
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      sessionStorage.setItem(codeVerifierKey, codeVerifier);
      sessionStorage.setItem(stateKey, state);
      sessionStorage.setItem(serviceKey, service);

      window.location.href = buildAuthorizeUrl({
        state,
        codeChallenge,
        service,
      });
    } catch {
      sessionStorage.removeItem(codeVerifierKey);
      sessionStorage.removeItem(stateKey);
      sessionStorage.removeItem(serviceKey);
      showConnectionToast({
        serviceName: service,
        status: 'error',
        message: loginFailedMessage,
      });
    }
  };

  const disconnect = async (
    deleteConversations: boolean,
    service = serviceName
  ) => {
    const res = await deleteDisconnect(service);

    if (res.data) {
      refetchServices();
      showConnectionToast({
        serviceName: service,
        action: 'disconnect',
        status: 'success',
        deleteConversations,
      });
      return true;
    }

    showConnectionToast({
      serviceName: service,
      action: 'disconnect',
      status: 'error',
    });
    return false;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once on mount to consume the OAuth redirect's code/state query params
  useEffect(() => {
    const { code, state } = params;

    // Every provider's hook is mounted together and sees the same code/state
    // params, so only the one whose callback route this is may consume them.
    const isOwnCallback =
      window.location.pathname === new URL(redirectUri).pathname;

    if (!code || !state || !isOwnCallback) {
      return;
    }

    const storedState = sessionStorage.getItem(stateKey);
    const codeVerifier = sessionStorage.getItem(codeVerifierKey);
    const service = sessionStorage.getItem(serviceKey) ?? serviceName;

    sessionStorage.removeItem(stateKey);
    sessionStorage.removeItem(codeVerifierKey);
    sessionStorage.removeItem(serviceKey);

    // Must go through React Router's own APIs rather than
    // window.history.replaceState — bypassing the router leaves its search
    // params stale, so a later setSearchParams call (e.g. switching tabs)
    // restores code/state and this effect fires again on remount with no
    // verifier left, showing a false failure toast after a successful connect.
    deleteQueryParam(['code', 'state'], { replace: true });

    if (!codeVerifier || state !== storedState) {
      showConnectionToast({
        serviceName: service,
        status: 'error',
        message: connectionFailedMessage,
      });
      return;
    }

    postConnect({ extra: { code, codeVerifier, redirectUri } }, service).then(
      res => {
        if (res.data?.connected) {
          refetchServices();
        }

        showConnectionToast({
          serviceName: service,
          status: res.data?.connected ? 'success' : 'error',
          message: res.data?.connected
            ? ''
            : getErrorMessage(connectionFailedMessage, res.error?.message),
        });
      }
    );
  }, []);

  return { connect, disconnect };
};
